import React, { useState } from 'react';
import { ArenaLang, useT } from '../i18n';

export type ArenaMode = 'literatur' | 'sachtext';

interface LandingProps {
  onStart: (mode: ArenaMode) => void;
  onShowLeaderboard: () => void;
  lang: ArenaLang;
  onChangeLang: (lang: ArenaLang) => void;
}

export default function Landing({ onStart, onShowLeaderboard, lang, onChangeLang }: LandingProps) {
  const [mode, setMode] = useState<ArenaMode>('literatur');
  const t = useT();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
      <div className="max-w-2xl">
        {/* Language switcher */}
        <div className="flex justify-center gap-2 mb-8">
          {(['de', 'en'] as const).map((l) => (
            <button
              key={l}
              onClick={() => onChangeLang(l)}
              className={`px-3 py-1 text-xs font-sans rounded transition-colors ${
                lang === l
                  ? 'bg-stone-200 text-stone-700 font-medium'
                  : 'text-stone-400 hover:text-stone-600'
              }`}
            >
              {l === 'de' ? 'Deutsch' : 'English'}
            </button>
          ))}
        </div>

        <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 text-stone-800 italic">
          Text Arena
        </h1>

        <div className="w-16 h-px bg-stone-300 mx-auto mb-6" />

        <p className="font-serif text-xl md:text-2xl text-stone-700 mb-4 leading-relaxed">
          {t('landing.subtitle')}
        </p>

        <p className="font-serif text-stone-500 mb-12 max-w-lg mx-auto leading-relaxed">
          {t('landing.description')}
        </p>

        <button
          onClick={() => onStart(mode)}
          className="px-10 py-4 bg-stone-800 hover:bg-stone-900 text-white text-lg font-display font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg shadow-stone-300"
        >
          {t('landing.start')}
        </button>

        {/* Subtle mode switch */}
        <p className="mt-6 text-xs text-stone-400 font-serif">
          {mode === 'literatur' ? (
            <>
              {t('landing.modePrefix')}: {t('landing.modeLiteratur')}&ensp;·&ensp;
              <button
                onClick={() => setMode('sachtext')}
                className="underline hover:text-stone-600 transition-colors"
              >
                {t('landing.switchToSachtext')}
              </button>
            </>
          ) : (
            <>
              {t('landing.modePrefix')}: {t('landing.modeSachtext')}&ensp;·&ensp;
              <button
                onClick={() => setMode('literatur')}
                className="underline hover:text-stone-600 transition-colors"
              >
                {t('landing.switchToLiteratur')}
              </button>
            </>
          )}
        </p>

        <p className="mt-10 text-xs text-stone-400 font-sans">
          <button
            onClick={onShowLeaderboard}
            className="underline hover:text-stone-600 transition-colors"
          >
            Leaderboard
          </button>
          &ensp;·&ensp;{t('landing.experiment')}{' '}
          <a
            href="https://hermes3000.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-stone-600"
          >
            Hermes 3000
          </a>
          &ensp;·&ensp;
          <a
            href="https://hermes3000.ai/imprint"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-stone-600"
          >
            {t('legal.imprint')}
          </a>
          &ensp;·&ensp;
          <a
            href="https://hermes3000.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-stone-600"
          >
            {t('legal.privacy')}
          </a>
        </p>
      </div>
    </div>
  );
}
